import Layout from 'components/ui/Layout';
import { PAGE_CONTENT_WIDTH } from 'utils/uiHelpers';
import PageHeader from 'components/ui/PageHeader';
import { useSellers } from 'features/seller/hooks/useSellers';
import { usePrefetchSellers } from 'features/seller/hooks/usePrefetchSellers';
import { useDeleteSeller } from 'features/seller/hooks/useDeleteSeller';
import {
  ChevronLeft,
  ChevronRight,
  Download,
  Edit,
  Eye,
  Search,
  Trash2,
  UserPlus,
  X,
  User,
  Store,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { formatDateTime } from 'utils/dateFormatter';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { useMainCategories } from 'features/main-category/hooks/useMainCategories';

import EditSellerModal from 'features/seller/components/EditSellerModal';

import { sellerServices } from 'features/seller/services/seller.service';

import { useSubCategories } from 'features/sub-category/hooks/useSubCategories';

const SELLER_TYPES = [
  { key: 'all', label: 'All' },

  { key: 'service_provider', label: 'Owner' },

  { key: 'freelancer', label: 'Freelancer' },

  { key: 'staff', label: 'Staff' },

  // { key: 'tourism_guide', label: 'Tourism Guide' },
];

const UserManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get initial tab from URL or default to 'service_provider'
  const getInitialTab = () => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && SELLER_TYPES.some((t) => t.key === tabFromUrl)) {
      return tabFromUrl;
    }
    return 'service_provider';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);

  const { data: categoryResponse } = useMainCategories({
    limit: 100,
  });

  const categories =
    categoryResponse?.data?.filter(
      (category) =>
        !["food-and-beverages", "find-jobs"].includes(category.slug),
    ) || [];


  // Filter state synced with URL
  const [filters, setFilters] = useState({
    page: searchParams.get('page') || '1',
    limit: searchParams.get('limit') || '10',
    status: searchParams.get('status') || 'all',
    kycStatus: searchParams.get('kycStatus') || 'all',
    phoneVerified: searchParams.get('phoneVerified') || 'all',
    emailVerified: searchParams.get('emailVerified') || 'all',
    search: searchParams.get('search') || '',
    sortBy: searchParams.get('sortBy') || 'createdAt',
    order: searchParams.get('order') || 'desc',
    categoryId: searchParams.get('categoryId') || '',   // ✅ add this
      subcategoryId: searchParams.get('subcategoryId') || '',

  });

  const { data: subCategoryResponse } = useSubCategories({
    parentCategory: filters.categoryId,
    limit: 100,
  });

const subCategories = subCategoryResponse?.data || [];

  const [searchInput, setSearchInput] = useState(filters.search);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch sellers with sellerType filter
  // const { data, isPending, isError, error } = useSellers({
  //   ...filters,
  //   sellerType: activeTab,
  // });

  const { data, isPending, isError, error } = useSellers({
  ...filters,
  sellerType: activeTab,
});
  const sellers = data?.data?.sellers;
  const pagination = data?.data?.pagination;

  const { mutate: deleteSeller, isPending: isDeleting } =
  useDeleteSeller();

  // Prefetch
  const { prefetchNextPage, prefetchPrevPage } = usePrefetchSellers();

  // Prefetch adjacent pages
  useEffect(() => {
    if (pagination) {
      const { currentPage, totalPages } = pagination;
      prefetchNextPage({ ...filters, sellerType: activeTab }, totalPages);
      if (currentPage > 1) {
        prefetchPrevPage({ ...filters, sellerType: activeTab });
      }
    }
  }, [data, filters, activeTab]);

  // Sync URL with filters and tab
  useEffect(() => {
    const params = new URLSearchParams();
    params.set('tab', activeTab);
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all' && value !== '') {
        params.set(key, value);
      }
    });
    setSearchParams(params, { replace: true });
  }, [filters, activeTab, setSearchParams]);

  // Update tab from URL changes
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && SELLER_TYPES.some((t) => t.key === tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Update filter and reset to page 1
  const updateFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: key === 'page' ? value : '1',
    }));
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setFilters((prev) => ({
      ...prev,
      page: '1',
    }));
  };

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        updateFilter('search', searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setSearchInput(filters.search);
  }, [filters.search]);

  // Sort handler
  const handleSort = (column) => {
    const newOrder =
      filters.sortBy === column && filters.order === 'asc' ? 'desc' : 'asc';
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      order: newOrder,
    }));
  };

  // Export handler
const handleExport = async () => {
  try {
    const response =
      await sellerServices.exportSellers(
        {
          ...filters,

          sellerType:
            activeTab,

          order:
            filters.order,
        }
      );

    const blob = new Blob(
      [response.data],
      {
        type: 'text/csv',
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download = `${activeTab}-sellers-${Date.now()}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    window.URL.revokeObjectURL(
      url
    );
  } catch (error) {
    console.error(
      'Failed to export sellers',
      error
    );
  }
};

  const SortIcon = ({ column }) => {
    if (filters.sortBy !== column)
      return <span className='text-muted-foreground'>↕</span>;
    return filters.order === 'asc' ? <span>↑</span> : <span>↓</span>;
  };

  const handleEditClick = (user) => {
    setIsEditModalOpen(true);
    setSelectedUser(user);
  };

const handleDeleteClick = (user) => {
  setSelectedUser(user);
  setIsDeleteModalOpen(true);
};

const handleConfirmDelete = () => {
  if (!selectedUser?._id) return;

  deleteSeller(selectedUser._id, {
    onSuccess: () => {
      setIsDeleteModalOpen(false);
      setSelectedUser(null);
    },
    onError: () => {
      setIsDeleteModalOpen(false);
    },
  });
};

  const handleViewClick = (user) => {
    navigate(`/sellers/${user._id}`);
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-500/10 text-green-600 dark:text-green-400',
      inactive: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
      suspended: 'bg-red-500/10 text-red-600 dark:text-red-400',
      rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
      blocked: 'bg-red-500/10 text-red-600 dark:text-red-400',
      pending_approval: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getKycStatusColor = (status) => {
    const colors = {
      approved: 'bg-green-500/10 text-green-600 dark:text-green-400',
      pending: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      under_review: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      rejected: 'bg-red-500/10 text-red-600 dark:text-red-400',
      expired: 'bg-orange-500/10 text-orange-600 dark:text-orange-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getVerificationBadge = (verified) => {
    return verified ? (
      <span className='px-2 py-1 text-xs font-medium rounded-full bg-green-500/10 text-green-600 dark:text-green-400'>
        Verified
      </span>
    ) : (
      <span className='px-2 py-1 text-xs font-medium rounded-full bg-gray-500/10 text-gray-600 dark:text-gray-400 whitespace-nowrap'>
        Not Verified
      </span>
    );
  };

  return (
    <Layout>
      <div className={`min-h-screen bg-background text-foreground p-6 ${PAGE_CONTENT_WIDTH}`}>
          {/* Header */}
          <PageHeader
            icon={Store}
            title="Service Provider Management"
            subtitle="Manage service providers, freelancers and staff"
            actions={
              !isError && (
                <button
                  onClick={handleExport}
                  className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
                >
                  <Download size={16} />
                  <span>Export</span>
                </button>
              )
            }
          />

          {/* Tabs */}
          <div className='mb-6'>
            <div className='border-b border-border'>
              <nav className='-mb-px flex space-x-8'>
                {SELLER_TYPES.map((type) => (
                  <button
                    key={type.key}
                    onClick={() => handleTabChange(type.key)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                      activeTab === type.key
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    {type.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Filters */}
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6'>
            {/* Search */}
            <div className='relative lg:col-span-2'>
              <Search
                className='absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground'
                size={16}
              />
              <input
                type='text'
                placeholder='Search by name, email or phone...'
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className='w-full pl-10 pr-10 py-2 text-sm'
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className='absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground'
                >
                  <X size={16} />
                </button>
              )}
            </div>

            

            {/* category wise filter */}

            <select
              value={filters.categoryId}
              // onChange={(e) => updateFilter('categoryId', e.target.value)}
              onChange={(e) => {
  updateFilter('categoryId', e.target.value);
  updateFilter('subcategoryId', '');
}}
              className='px-3 py-2 text-sm'
            >
              <option value=''>All Categories</option>
              {categories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name?.english}
                </option>
              ))}
            </select>

{/* sub category wise filter */}


            <select
  value={filters.subcategoryId}
  onChange={(e) =>
    updateFilter('subcategoryId', e.target.value)
  }
  className="px-3 py-2 text-sm"
  disabled={!filters.categoryId}
>
  <option value="">All Sub Categories</option>

  {subCategories.map((subcategory) => (
    <option
      key={subcategory._id}
      value={subcategory._id}
    >
      {subcategory.name?.english}
    </option>
  ))}
</select>

              {/* Status Filter */}
            <select
              value={filters.status}
              onChange={(e) => updateFilter('status', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>All Status</option>
              <option value='active'>Active</option>
              <option value='inactive'>Inactive</option>
              {/* <option value='suspended'>Suspended</option> */}
              <option value='rejected'>Rejected</option>
              <option value='blocked'>Blocked</option>
              <option value='pending_approval'>Pending Approval</option>
            </select>

            <div className='w-full flex gap-3'> 

            {/* KYC Status Filter */}
            <select
              value={filters.kycStatus}
              onChange={(e) => updateFilter('kycStatus', e.target.value)}
              className='px-3 py-2 text-sm flex-1'
            >
              <option value='all'>All KYC Status</option>
              <option value='pending'>Pending</option>
              <option value='under_review'>Under Review</option>
              <option value='approved'>Approved</option>
              <option value='rejected'>Rejected</option>
              <option value='expired'>Expired</option>
            </select>

            {/* Phone Verified Filter */}
            {/* <select
              value={filters.phoneVerified}
              onChange={(e) => updateFilter('phoneVerified', e.target.value)}
              className='px-3 py-2 text-sm'
            >
              <option value='all'>Phone Status</option>
              <option value='true'>Phone Verified</option>
              <option value='false'>Phone Not Verified</option>
            </select> */}

            
            

            {/* Items per page */}
            <select
              value={filters.limit}
              onChange={(e) => updateFilter('limit', e.target.value)}
              className='px-3 py-2 text-sm'
              style={{minWidth:"108px"}}
            >
              <option value='5'>5 per page</option>
              <option value='10'>10 per page</option>
              <option value='20'>20 per page</option>
              <option value='50'>50 per page</option>
            </select>
            </div>

          </div>

          {/* Table */}
          <div className='bg-card border border-border rounded-lg overflow-hidden'>
            <div className='overflow-x-auto'>
              <table className='w-full'>
                <thead className='bg-muted border-b border-border'>
                  <tr>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      S.No
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Photo
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('firstName')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Name</span>
                        <SortIcon column='firstName' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Phone
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Email
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Service Category
                    </th>

                      <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Sub-Category
                    </th>
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Phone Verified
                    </th> */}
                    {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      Email Verified
                    </th> */}
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('status')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Status</span>
                        <SortIcon column='status' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase'>
                      KYC Status
                    </th>
                    <th
                      className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase cursor-pointer hover:text-foreground'
                      onClick={() => handleSort('createdAt')}
                    >
                      <div className='flex items-center space-x-1'>
                        <span>Created</span>
                        <SortIcon column='createdAt' />
                      </div>
                    </th>
                    <th className='px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase'>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className='divide-y divide-border'>
                  {isPending ? (
                    <tr>
                      <td
                        colSpan='11'
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        Loading users...
                      </td>
                    </tr>
                  ) : isError ? (
                    <tr>
                      <td
                        colSpan='11'
                        className='px-4 py-8 text-center text-destructive'
                      >
                        Error loading users:{' '}
                        {error?.message || 'Something went wrong'}
                      </td>
                    </tr>
                  ) : sellers?.length === 0 ? (
                    <tr>
                      <td
                        colSpan='11'
                        className='px-4 py-8 text-center text-muted-foreground'
                      >
                        No users found
                      </td>
                    </tr>
                  ) : (
                    sellers?.map((user, idx) => (
                      <tr
                        key={user._id}
                        className='hover:bg-muted/50 transition-colors'
                      >
                      <td className='px-4 py-3 text-sm text-muted-foreground'>
                        {((pagination?.currentPage || 1) - 1) * (pagination?.itemsPerPage || 10) + idx + 1}
                      </td>
                      <td className='px-4 py-3'>
  {user.profilePhoto ? (
    <img
      src={user.profilePhoto}
      alt={`${user.firstName || ''} ${user.lastName || ''}`}
      className='w-10 h-10 rounded-full object-cover border border-border'
    />
  ) : (
    <div className='w-10 h-10 rounded-full bg-muted flex items-center justify-center border border-border'>
      <User className='w-5 h-5 text-muted-foreground' />
    </div>
  )}
</td>
                        <td className='px-4 py-3 text-sm font-medium text-foreground whitespace-nowrap'>
                          {user.firstName || user.lastName
                            ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                            : 'N/A'}
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground'>
                          {user.countryCode}
                          {user.phoneNumber}
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground'>
                          {user.email || 'N/A'}
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground'>
                          <div
                            className='max-w-[160px] truncate'
                            title={user?.categories?.name?.english || 'N/A'}
                          >
                            {user?.categories?.name?.english || 'N/A'}
                          </div>
                        </td>

                         <td className='px-4 py-3 text-sm text-foreground'>
                          <div
                            className='max-w-[160px] truncate'
                            title={user?.sub_categories[0]?.name?.english || 'N/A'}
                          >
                            {user?.sub_categories[0]?.name?.english || 'N/A'}
                          </div>
                        </td>
                        {/* <td className='px-4 py-3 text-sm'>
                          {getVerificationBadge(user.phoneVerified)}
                        </td> */}
                        {/* <td className='px-4 py-3 text-sm'>
                          {getVerificationBadge(user.emailVerified)}
                        </td> */}
                        <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full capitalize whitespace-nowrap ${getStatusColor(
                              user.status,
                            )}`}
                          >
                            {user.status?.replace('_', ' ') || 'N/A'}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-sm'>
                          <span
                            className={`px-2 py-1 text-xs font-medium rounded-full capitalize whitespace-nowrap ${getKycStatusColor(
                              user.kyc?.status,
                            )}`}
                          >
                            {user.kyc?.status?.replace('_', ' ') || 'N/A'}
                          </span>
                        </td>
                        <td className='px-4 py-3 text-sm text-foreground'>
                          {formatDateTime(user.createdAt)}
                        </td>
                        <td className='px-4 py-3 text-sm text-right'>
                          <div className='flex items-center justify-end space-x-2'>
                            <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='View Details'
                              onClick={() => handleViewClick(user)}
                            >
                              <Eye
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                           <button
                              className='p-1 hover:bg-muted rounded transition-colors'
                              title='Edit User'
                              onClick={() => handleEditClick(user)}
                            >
                              <Edit
                                size={16}
                                className='text-muted-foreground hover:text-foreground'
                              />
                            </button>
                           <button
  onClick={() => handleDeleteClick(user)}
  disabled={isDeleting}
  className='p-1 hover:bg-muted rounded transition-colors disabled:opacity-50'
  title='Delete User'
>
                              <Trash2 size={16} className='text-muted-foreground hover:text-destructive' />
                            </button> 
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination && (
              <div className='flex flex-col sm:flex-row items-center justify-between px-4 py-3 border-t border-border space-y-3 sm:space-y-0'>
                <div className='text-sm text-muted-foreground'>
                  Showing{' '}
                  {(pagination.currentPage - 1) * pagination.itemsPerPage + 1}{' '}
                  to{' '}
                  {Math.min(
                    pagination.currentPage * pagination.itemsPerPage,
                    pagination.totalItems,
                  )}{' '}
                  of {pagination.totalItems} users
                </div>

                <div className='flex items-center space-x-2'>
                  <button
                    onClick={() =>
                      updateFilter('page', String(pagination.currentPage - 1))
                    }
                    disabled={pagination.currentPage === 1}
                    className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <div className='flex items-center space-x-1'>
                    {[...Array(pagination.totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      const current = pagination.currentPage;

                      // Show first, last, current, and adjacent pages
                      if (
                        page === 1 ||
                        page === pagination.totalPages ||
                        (page >= current - 1 && page <= current + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => updateFilter('page', String(page))}
                            className={`min-w-[2rem] px-3 py-1 text-sm rounded-md transition-colors ${
                              page === current
                                ? 'bg-primary text-primary-foreground font-medium'
                                : 'bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === current - 2 || page === current + 2) {
                        return (
                          <span key={page} className='text-muted-foreground'>
                            ...
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>

                  <button
                    onClick={() =>
                      updateFilter('page', String(pagination.currentPage + 1))
                    }
                    disabled={pagination.currentPage === pagination.totalPages}
                    className='p-2 border border-border rounded-md bg-card text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

      </div>
      {isEditModalOpen && selectedUser && (
  <EditSellerModal
    seller={selectedUser}
    onClose={() => {
      setIsEditModalOpen(false);
      setSelectedUser(null);
    }}
  />
)}

{isDeleteModalOpen && selectedUser && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
    <div className="w-full max-w-md rounded-lg bg-card border border-border shadow-xl">
      
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-border">
        <h3 className="text-lg font-semibold text-foreground">
          Delete Seller
        </h3>

        <button
          onClick={() => {
            setIsDeleteModalOpen(false);
            setSelectedUser(null);
          }}
          disabled={isDeleting}
          className="text-muted-foreground hover:text-foreground"
        >
          <X size={18} />
        </button>
      </div>

      {/* Body */}
      <div className="p-5">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-500" />
          </div>

          <div>
            <p className="text-sm text-foreground">
              Are you sure you want to permanently delete:
            </p>

            <p className="font-semibold mt-2 text-foreground">
              {selectedUser.firstName} {selectedUser.lastName}
            </p>

            <p className="text-sm text-muted-foreground mt-1">
              {selectedUser.email || selectedUser.phoneNumber}
            </p>

            <p className="text-sm text-red-500 mt-4">
              This action cannot be undone. All seller related data
              will be permanently removed.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-3 p-5 border-t border-border">
        <button
          onClick={() => {
            setIsDeleteModalOpen(false);
            setSelectedUser(null);
          }}
          disabled={isDeleting}
          className="px-4 py-2 border border-border rounded-md hover:bg-muted transition-colors"
        >
          Cancel
        </button>

        <button
          onClick={handleConfirmDelete}
          disabled={isDeleting}
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
        >
          {isDeleting ? 'Deleting...' : 'Delete Seller'}
        </button>
      </div>
    </div>
  </div>
)}
    </Layout>
  );
};

export default UserManagement;
